import styled from '@emotion/styled';

export const AvatarChip = styled.button`
  flex-shrink: 0;
  height: 48px;
  padding: 4px 6px 4px 4px;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey6};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.75)};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.neutrals.grey1};
  }
`;

export const Initials = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.neutrals.grey9};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-weight: 700;
  font-family: NovemberHebrew;
  font-size: 16px;
  text-transform: uppercase;
  line-height: 22px;
`;

// the codebase's kebab/more-options asset (menu-dots.svg) is a horizontal 3-dot layout —
// rotated 90deg to read as the vertical kebab Figma shows, since no vertical asset exists.
// Sized up to 20px (from the asset's intrinsic 16px) so the dots match Figma's fuller glyph
// (~17.5px span) inside the 24px slot.
export const KebabIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 20px;
    height: 20px;
    transform: rotate(90deg);
  }
`;

export const MenuContainer = styled.div`
  position: relative;
`;

// matches Figma's own icon wrapper (the "Pencil" div, node 10952:51330: "relative shrink-0
// size-[24px]") — every row icon sits in a fixed 24x24 slot, centered, regardless of its own
// non-square native proportions. iconWidth sizes the actual glyph within that slot (height:auto
// preserves its true aspect ratio instead of squashing it); defaults to 24 for square icons.
export const MenuIcon = styled.div<{ iconWidth?: number }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: ${({ iconWidth = 24 }) => iconWidth}px;
    height: auto;
  }
`;

// the avatar chip now sits at the header's physical LEFT edge (per the header-mirror fix), so the
// dropdown must open TOWARD the page (physical right), not further left off the viewport edge.
// inset-inline-end (not -start) resolves to physical "left" under this app's dir="rtl" — anchoring
// the dropdown's left edge there and letting it grow rightward, back onto the visible page
// (confirmed live: the previous -start anchor pinned the dropdown's right edge to the chip and
// grew it 230px further left, which went off-screen once the chip moved to the left edge)
// exact values confirmed via get_design_context on Figma node 10952:51330 ("avatar_menu"): no
// border, two-layer "Level 7" shadow, 8px row gap — not the placeholder single-layer shadow +
// grey6 border used in the first pass
export const DesktopDropdown = styled.div`
  position: absolute;
  top: calc(100% + ${({ theme }) => theme.spacing(1)});
  inset-inline-end: 0;
  z-index: 10;
  box-sizing: border-box;
  width: 230px;
  padding: ${({ theme }) => theme.spacing(1)};
  background: ${({ theme }) => theme.colors.neutrals.white};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.05), 0px 2px 5px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

// padding is intentionally asymmetric per Figma (node 10952:51330): 8px on the icon side
// (physical-right/inline-start, where the icon sits close to the row edge) vs 24px on the far
// side (physical-left/inline-end, where the right-aligned text trails off into empty space) —
// confirmed via the row content's exact offsets (left:24, width:182 inside a 214px row).
// Icon sizing itself lives on MenuIcon (the row's icon wrapper), not here.
export const MenuRow = styled.button`
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding-inline-start: ${({ theme }) => theme.spacing(1)};
  padding-inline-end: ${({ theme }) => theme.spacing(3)};
  border: none;
  background: transparent;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-weight: 400;
  font-family: NovemberHebrew;
  font-size: 16px;
  line-height: 22px;
  text-align: right;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.neutrals.grey10};
  }
`;

// mobile: user-icon button that opens the bottom-sheet menu (see Modal usage in AvatarMenu.tsx)
export const MobileMenuButton = styled.button`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey6};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.neutrals.white};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.neutrals.grey1};
  }

  > svg {
    width: 24px;
    height: 24px;
  }
`;

export const MobileMenuList = styled.div`
  display: flex;
  flex-direction: column;
`;

// icon renders first in JSX (physically rightmost under RTL) with text right after it, both at
// their natural width — a compact group packed toward the row's start, same confirmed-correct
// pattern as SavedHoodsV2's title/chevron row (deliberately NOT justify-content:flex-end, which
// would push the whole group to the wrong physical side under RTL).
// Icon sizing itself lives on MenuIcon (the row's icon wrapper), not here. No border-top — Figma's
// own screenshot (11189:104750) confirms a completely clean list with no dividers between rows.
export const MobileMenuRow = styled.button`
  box-sizing: border-box;
  width: 100%;
  height: 56px;
  padding: 0 ${({ theme }) => theme.spacing(3)};
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-weight: 400;
  font-family: NovemberHebrew;
  font-size: 16px;
  line-height: 22px;
  cursor: pointer;
`;

// custom close header replacing the shared Modal's own built-in close button — that one has no
// dedicated header row at all (a small icon floats with a *negative* offset above the sheet's
// edge) and uses a filled solid-X icon (close-modal.svg), neither of which match Figma's confirmed
// design: a clean 56px white header bar with rounded top corners matching the sheet's own, and a
// thin two-line stroke X inset from the physical-left edge. Built here instead of touching the
// shared Modal (used site-wide) — passed via Modal's `hideCloseButton` prop.
export const MobileMenuHeader = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  height: 56px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.neutrals.white};
  border-radius: ${({ theme }) => theme.borderRadius.large} ${({ theme }) => theme.borderRadius.large} 0 0;
`;

// Figma's own coordinate space (left:8) is physical-left, plain-LTR, same convention established
// all session — inset-inline-end (not -start) resolves to physical LEFT under this app's real
// dir="rtl" (confirmed live: -start landed the button on the physical right, the wrong side)
export const MobileMenuCloseButton = styled.button`
  position: absolute;
  inset-inline-end: ${({ theme }) => theme.spacing(1)};
  top: ${({ theme }) => theme.spacing(1)};
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 24px;
    height: 24px;
  }
`;
